import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isFinishedIssueStatus, type TicketSeverity, type TicketStatus } from '@overlayqa/shared';
import { useApiMutation } from './useApiMutation';
import { ticketsApi } from '../lib/api';
import { ISSUE_MUTATION_INVALIDATE_KEYS, ISSUES_KEY } from './issueMutationInvalidation';
import { log } from '@/lib/logger';

log.info('[useInlineIssueUpdate] Module loaded');

/**
 * The copy a refused save already shows on the issue detail page
 * (TicketDetailsCard.tsx:136). One product, one sentence for one failure: a
 * second wording here would teach the reader that these are different faults.
 */
const SAVE_FAILED = 'Failed to save changes. Please try again.';

/**
 * The fields the issues table can change in place. Narrow on purpose:
 * this hook writes the screen BEFORE the server answers, which is only safe for
 * a field whose new value is fully known up front. A title or a description is
 * edited on the detail page, where `useUpdateIssue` saves it the ordinary way.
 */
export interface InlineIssueFields {
  severity?: TicketSeverity;
  type?: string;
  status?: TicketStatus;
  assigneeId?: string | null;
}

interface InlineIssueUpdate {
  id: string;
  data: InlineIssueFields;
  /** Rides the PATCH only; never written into the optimistic row. */
  via?: 'not_detected_chip';
}

/**
 * The cached shape at an `['issues', ...]` key that is a LIST rather than one
 * ticket. `useIssue(id)` caches a bare Ticket under the same prefix and a
 * Ticket has no `items`, so checking for `items` is what tells the two apart
 * without corrupting the detail cache (the trap useBulkActions.ts:108 documents).
 */
interface IssuesListCache {
  items?: unknown[];
}

/** What we wrote, where we wrote it, and what was there before. */
/** The written fields, plus the not-detected flag a finished status clears server-side. */
type AppliedFields = InlineIssueFields & { notDetectedAt?: null };

type OptimisticWrite = Array<{
  id: string;
  key: QueryKey;
  previous: Record<string, unknown>;
  applied: AppliedFields;
}>;

function applyOptimistically(queryClient: QueryClient, { id, data }: InlineIssueUpdate): OptimisticWrite {
  const written: OptimisticWrite = [];
  // Mirror PATCH /issues/:id: a finished status clears not_detected_at, so an
  // Undo that reopens the row does not briefly bring back a stale chip.
  const fields: AppliedFields = data.status && isFinishedIssueStatus(data.status) ? { ...data, notDetectedAt: null } : { ...data };
  for (const [key, old] of queryClient.getQueriesData<IssuesListCache>({ queryKey: ['issues'] })) {
    if (!Array.isArray(old?.items)) continue;
    let previous: Record<string, unknown> | null = null;
    const items = old.items.map((item) => {
      const row = item as { id?: string } | null;
      if (!row || row.id !== id) return item;
      previous = Object.fromEntries(Object.keys(fields).map((field) => [field, (row as Record<string, unknown>)[field]]));
      return { ...row, ...fields };
    });
    if (!previous) continue;
    written.push({ id, key, previous, applied: fields });
    queryClient.setQueryData(key, { ...old, items });
  }
  return written;
}

/**
 * Undo our own write, and only ours. A field whose cached value is no longer
 * the one we wrote has been changed by something newer than this request (a
 * refetch, a teammate, another cell on the same row), and restoring it would
 * replace a fresher truth with a stale one.
 */
function rollback(queryClient: QueryClient, written: OptimisticWrite | undefined): void {
  for (const { id, key, previous, applied } of written ?? []) {
    queryClient.setQueryData<IssuesListCache>(key, (current) => {
      if (!Array.isArray(current?.items)) return current;
      let changed = false;
      const items = current.items.map((item) => {
        const row = item as Record<string, unknown> | null;
        if (!row || row.id !== id) return item;
        const restore = Object.entries(previous).filter(
          ([field, was]) => row[field] === (applied as Record<string, unknown>)[field] && row[field] !== was,
        );
        if (restore.length === 0) return item;
        changed = true;
        return { ...row, ...Object.fromEntries(restore) };
      });
      return changed ? { ...current, items } : current;
    });
  }
}

/**
 * Change one field of one issue from the table, with no Save button.
 *
 * The badge shows the new value at once and the request settles behind it; a
 * refusal puts the old value back and says so. That is the whole difference
 * from `useUpdateIssue` (useTickets.ts:79), which the detail page uses and
 * which must stay non-optimistic: that page already renders the edited value
 * from local state and already toasts its own failures.
 *
 * Contract pinned by __tests__/useInlineIssueUpdate.test.tsx, watched failing
 * against a plain save first.
 */
export function useInlineIssueUpdate() {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, InlineIssueUpdate, OptimisticWrite>({
    mutationFn: (token, { id, data, via }) => ticketsApi.update(token, id, via ? { ...data, via } : data),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    onMutate: async (variables) => {
      // A refetch an earlier write started can answer AFTER this write's
      // optimistic value is on screen, carrying this row's OLD value, and would
      // put it back for a whole round trip (measured 2026-10-23 once the board
      // stopped waiting on refetches, IssueBoard.dropSettle.test.tsx). Discard
      // it: this write's own invalidation refetches once it lands. The prefix
      // also covers an in-flight single-ticket read, which refetches when next
      // observed.
      await queryClient.cancelQueries({ queryKey: ISSUES_KEY });
      return applyOptimistically(queryClient, variables);
    },
    onError: (error, variables, context) => {
      log.error('[useInlineIssueUpdate] refused, rolling back:', { id: variables.id, message: error.message });
      rollback(queryClient, context);
      // The server's own sentence when it wrote one (a 409 names the teammate
      // conflict); the shipped fallback when the failure has nothing to say.
      toast.error(error.message || SAVE_FAILED);
    },
  });
}
